// src/pages/artist-dashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { auth, db } from '../firebase';
import { ref, onValue } from 'firebase/database';
import ArtistDashboard from '../components/artist/ArtistDashboard';

export default function ArtistDashboardPage({ showToast }) {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [artistData, setArtistData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      
      if (user) {
        // Fetch artist data
        const artistRef = ref(db, `artists/${user.uid}`);
        const artistUnsubscribe = onValue(artistRef, (snapshot) => {
          const data = snapshot.val();
          setArtistData(data);
          setLoading(false);
        });

        return () => artistUnsubscribe();
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-b-purple-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4"
      >
        <div className="text-center max-w-md">
          <AlertCircle size={64} className="mx-auto mb-4 text-red-600" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Not Logged In</h1>
          <p className="text-gray-700 mb-6">
            You need to sign in to access your artist dashboard.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
          >
            Go to Home & Sign In
          </button>
        </div>
      </motion.div>
    );
  }

  if (!artistData) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50 p-4"
      >
        <div className="text-center max-w-md">
          <AlertCircle size={64} className="mx-auto mb-4 text-yellow-600" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">No Artist Account</h1>
          <p className="text-gray-700 mb-6">
            You don't have an artist account yet. Register to become an artist!
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
          >
            Go to Home & Register
          </button>
        </div>
      </motion.div>
    );
  }

  if (artistData.status !== "active") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4"
      >
        <div className="text-center max-w-md bg-white rounded-xl shadow-lg p-8">
          <AlertCircle size={64} className="mx-auto mb-4 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Pending</h1>
          <p className="text-gray-700 mb-6">
            Your artist account is not yet active. Please check your email for updates from our admin team.
          </p>
          <button
            onClick={() => navigate("/")}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
          >
            Back to Home
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold mb-6 transition"
        >
          <ArrowLeft size={20} />
          Back to Home
        </motion.button>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <ArtistDashboard
            db={db}
            currentUser={currentUser}
            showToast={showToast}
          />
        </motion.div>
      </div>
    </div>
  );
}